import React, { useCallback, useEffect, useState } from "react";
import {
  MatchesIcon,
  MarketsIcon,
  CalendarIcon,
  UsersIcon,
} from "../../components/admin/DashboardIcons.jsx";
import ReactApexChart from "react-apexcharts";
// import { m } from "framer-motion";

const Dashboard = () => {
  const [activeUsers, setActiveUsers] = useState(0);
  const [activeMarkets, setActiveMarkets] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  const [marketList, setMarketList] = useState([]);
  const [betTypes, setBetTypes] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);

  // const [search, setSearch] = useState("");
  // const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [selectedRange, setSelectedRange] = useState("today");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const getRangeDates = useCallback(
    (rangeKey) => {
      const now = new Date();
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      const toYmd = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };

      switch (rangeKey) {
        case "today":
          return { start: toYmd(end), end: toYmd(end) };
        case "yesterday": {
          const yest = new Date(end);
          yest.setDate(end.getDate() - 1);
          return { start: toYmd(yest), end: toYmd(yest) };
        }
        case "last7": {
          const s = new Date(end);
          s.setDate(end.getDate() - 6);
          return { start: toYmd(s), end: toYmd(end) };
        }
        case "thisMonth": {
          const s = new Date(end.getFullYear(), end.getMonth(), 1);
          return { start: toYmd(s), end: toYmd(end) };
        }
        case "last30": {
          const s = new Date(end);
          s.setDate(end.getDate() - 29);
          return { start: toYmd(s), end: toYmd(end) };
        }
        case "custom":
          return { start: customStart || "", end: customEnd || "" };
        default:
          return { start: toYmd(end), end: toYmd(end) };
      }
    },
    [customStart, customEnd]
  );

  // no need to memoize; compute inside effect when needed

  const getAdminDashboardData = async (range) => {
    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

    const urlencoded = {
      user_id: localStorage.getItem("uid"),
      start_date: range.start,
      end_date: range.end,
    };

    let requestOptions = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(urlencoded),
    };

    try {
      const response = await fetch(
        process.env.REACT_APP_API_HOST + "/totaldata",
        requestOptions
      );

      if (response.status === 401) {
        console.log("Unauthorized access. Please log in again.");
        setActiveUsers(0);
        setActiveMarkets(0);
        setTotalMatches(0);
        return;
      }

      const result = await response.json();
      
      if (result?.data) {
        setActiveUsers(result.data.activeUsers || 0);
        setActiveMarkets(result.data.totalMarkets || 0);
        setTotalMatches(result.data.totalMatches || 0);
      } else {
        setActiveUsers(0);
        setActiveMarkets(0);
        setTotalMatches(0);
      }
    } catch (err) {
      console.log("Error fetching dashboard data:", err);
      setActiveUsers(0);
      setActiveMarkets(0);
      setTotalMatches(0);
    }
  };

  const getAdminDashboardDataMarketwise = async (range) => {
    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

    const urlencoded = {
      user_id: localStorage.getItem("uid"),
      start_date: range.start,
      end_date: range.end,
    };

    let requestOptions = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(urlencoded),
    };

    try {
      const response = await fetch(
        process.env.REACT_APP_API_HOST + "/dashboard/marketwise",
        requestOptions
      );

      if (response.status === 401) {
        console.log("Unauthorized access. Please log in again.");
        setMarketList([]);
        return;
      }

      const result = await response.json();
      
      if (result?.data) {
        console.log("Marketwise data:", result.data);
        // Use functional update to ensure state is set properly
        setMarketList((prev) => {
          // Force React to recognize the update
          return Array.isArray(result.data) ? [...result.data] : [];
        });
      } else {
        setMarketList([]);
      }
    } catch (err) {
      console.log("Error fetching marketwise data:", err);
      setMarketList([]);
    }
  };

  const getAdminDashboardDataRecentMatches = async (range) => {
    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

    const urlencoded = {
      user_id: localStorage.getItem("uid"),
      start_date: range.start,
      end_date: range.end,
    };

    let requestOptions = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(urlencoded),
    };

    try {
      const response = await fetch(
        process.env.REACT_APP_API_HOST + "/recent/matches",
        requestOptions
      );

      if (response.status === 401) {
        console.log("Unauthorized access. Please log in again.");
        setRecentMatches([]);
        return;
      }

      const result = await response.json();
      
      if (result?.data) {
        setRecentMatches(Array.isArray(result.data) ? [...result.data] : []);
      } else {
        setRecentMatches([]);
      }
    } catch (err) {
      console.log("Error fetching recent matches:", err);
      setRecentMatches([]);
    }
  };

  const getAdminDashboardDataBetTypeDistribution = async (range) => {
    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

    const urlencoded = {
      user_id: localStorage.getItem("uid"),
      start_date: range.start,
      end_date: range.end,
    };

    let requestOptions = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(urlencoded),
    };

    try {
      const response = await fetch(
        process.env.REACT_APP_API_HOST + "/dashboard/bettype",
        requestOptions
      );

      if (response.status === 401) {
        console.log("Unauthorized access. Please log in again.");
        setBetTypes([
          { name: "Single", stat: 0 },
          { name: "Single Patti", stat: 0 },
          { name: "Double Patti", stat: 0 },
          { name: "Triple Patti", stat: 0 },
          { name: "Jodi", stat: 0 },
        ]);
        return;
      }

      const result = await response.json();
      
      if (result?.data) {
        const requiredNames = [
          "Single",
          "Single Patti",
          "Double Patti",
          "Triple Patti",
          "Jodi",
        ];
        const normalizedData = requiredNames.map((name) => {
          const found = result.data.find(
            (item) => item.name.toLowerCase() === name.toLowerCase()
          );
          return found || { name, stat: 0 };
        });
        setBetTypes([...normalizedData]);
      } else {
        setBetTypes([
          { name: "Single", stat: 0 },
          { name: "Single Patti", stat: 0 },
          { name: "Double Patti", stat: 0 },
          { name: "Triple Patti", stat: 0 },
          { name: "Jodi", stat: 0 },
        ]);
      }
    } catch (err) {
      console.log("Error fetching bet type distribution:", err);
      setBetTypes([
        { name: "Single", stat: 0 },
        { name: "Single Patti", stat: 0 },
        { name: "Double Patti", stat: 0 },
        { name: "Triple Patti", stat: 0 },
        { name: "Jodi", stat: 0 },
      ]);
    }
  };

  // useEffect(() => {
  //   const range = getRangeDates(selectedRange);
  //   // Skip fetch for invalid custom range
  //   if (selectedRange === "custom" && (!range.start || !range.end)) {
  //     return;
  //   }
  //   getAdminDashboardData(range);
  //   getAdminDashboardDataMarketwise(range);
  //   getAdminDashboardDataRecentMatches(range);
  //   getAdminDashboardDataBetTypeDistribution(range);
  // }, [selectedRange, customStart, customEnd, getRangeDates]);

  useEffect(() => {
    const fetchData = async () => {
      const range = getRangeDates(selectedRange);

      if (selectedRange === "custom" && (!range.start || !range.end)) {
        return;
      }

      // Reset states immediately to show loading state
      setMarketList([]);
      setBetTypes([]);
      setRecentMatches([]);
      setActiveUsers(0);
      setActiveMarkets(0);
      setTotalMatches(0);

      try {
        await Promise.all([
          getAdminDashboardData(range),
          getAdminDashboardDataMarketwise(range),
          getAdminDashboardDataRecentMatches(range),
          getAdminDashboardDataBetTypeDistribution(range),
        ]);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchData();
  }, [selectedRange, customStart, customEnd, getRangeDates]);

  // removed single-date display in favor of range selector

  // Pie chart configuration
  const pieChartOptions = {
    chart: {
      type: "pie",
      width: 350, // default for desktop/tablet
    },
    labels: marketList.map((item) => item.name),
    colors: [
      "#db9721",
      "#01015e",
      "#911111",
      "#0c8316",
      "#820d9f",
      "#088bab",
      "#a4c10f",
      "#b90fb2",
      "#363636",
    ],
    legend: {
      position: "bottom",
      fontSize: "12px",
    },
    dataLabels: {
      style: {
        fontSize: "12px", // default for desktop/tablet
      },
    },
    responsive: [
      {
        breakpoint: 768, // mobile
        options: {
          chart: {
            width: 400,
          },
          legend: {
            position: "bottom",
            fontSize: "9px",
          },
          dataLabels: {
            style: {
              fontSize: "6px",
            },
            dropShadow: {
              enabled: false,
            },
            offset: 0, // reduce gap between data labels
          },
        },
      },
      {
        breakpoint: 1279, // mobile
        options: {
          chart: {
            width: 500,
          },
          legend: {
            position: "bottom",
            fontSize: "9px",
          },
          dataLabels: {
            style: {
              fontSize: "6px",
            },
            dropShadow: {
              enabled: false,
            },
            offset: 0, // reduce gap between data labels
          },
        },
      },
    ],
  };

  const pieChartSeries = marketList.map((item) => parseInt(item.stat));
  console.log("Pie chart series:", pieChartSeries);

  // Bar chart configuration
  const barChartOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 1,
        horizontal: false,
        columnWidth: "45%",
        distributed: true,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      // type: "numeric",
      // min: 0,
      // max: 11,
      // tickAmount: 12,
      // decimalsInFloat: 0,
      labels: {
        formatter: function (value) {
          const index = parseInt(value);
          const label = betTypes[index] ? betTypes[index].name : "";
          return label || "";
        },
        rotate: -45,
        style: {
          fontSize: "12px",
        },
      },
    },
    colors: ["#FF6B6B", "#4ECDC4", "#f7b84b", "#7C3AED", "#2563EB"],
    legend: {
      show: false,
      // show: true,
      position: "right",
      offsetY: 40,
      markers: {
        width: 9,
        height: 8,
        radius: 4,
      },
      itemMargin: {
        horizontal: 15,
        vertical: 8,
      },
    },
    grid: {
      borderColor: "#f1f1f1",
    },
    tooltip: {
      theme: "dark",
      x: {
        formatter: function (value) {
          const index = parseInt(value);
          return betTypes[index] ? betTypes[index].name : value;
        },
      },
      y: {
        formatter: function (val) {
          return val + " bets";
        },
      },
    },
  };

  const barChartSeries = [
    {
      name: "Bets",
      data: betTypes.map((item, index) => ({
        x: index,
        y: parseInt(item.stat),
      })),
    },
  ];

  const formatTimeTo12Hour = (time24) => {
    if (!time24) return "--";
    const [hour, minute] = time24.split(":");
    const date = new Date();
    date.setHours(hour);
    date.setMinutes(minute);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div>
      <div className="flex justify-between mb-6">
        <div className="flex flex-col">
          <span className="font-semibold sm:text-md md:text-lg text-sm">
            Welcome {localStorage.getItem("name") || ""}!
          </span>
          <span className="text-m_gray1 sm:text-lg text-xs">
            {/* Here's what's happening with your store today. */}
          </span>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-md overflow-hidden border border-gray-200">
              <div className="text-m_blue px-3 py-3 flex items-center justify-center sm:text-sm xl:text-md text-xs">
                <CalendarIcon />
              </div>
              <select
                className="px-2 py-2 outline-none text-sm"
                value={selectedRange}
                onChange={(e) => setSelectedRange(e.target.value)}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 Days</option>
                <option value="thisMonth">This Month</option>
                <option value="last30">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            {selectedRange === "custom" && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  className="px-2 py-2 border border-gray-200 rounded"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                />
                <span className="text-gray-500 text-sm">to</span>
                <input
                  type="date"
                  className="px-2 py-2 border border-gray-200 rounded"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className="grid grid-cols-12 gap-6 min-h-[400px]"
        id="dashboard-container"
      >
        {/* Left Column - Pie Chart */}
        <div className="col-span-12 xl:col-span-4">
          <div className="bg-white rounded-lg shadow-md h-full flex flex-col">
            <div className="p-4 border-b border-gray-100">
              <h4 className="text-lg font-medium text-gray-900 mb-0">
                Market Distribution
              </h4>
            </div>
            {/* <div className="p-4 flex-grow">
              <ReactApexChart
                options={pieChartOptions}
                series={pieChartSeries}
                type="pie"
                height="100%"
              />
            </div> */}
            {marketList.length > 0 ? (
              <div className="flex justify-center items-center xl:block flex-grow">
                <ReactApexChart
                  key={`pie-${selectedRange}-${customStart}-${customEnd}`}
                  options={pieChartOptions}
                  series={pieChartSeries}
                  type="pie"
                  height="100%"
                />
              </div>
            ) : (
              <div className="text-center items-center py-5  text-gray-400">
                No data available
              </div>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="col-span-12 xl:col-span-8">
          <div className="flex flex-col h-full">
            {/* Stats Cards Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Total Invested Card */}
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-m_black rounded-full text-blue-600">
                    <UsersIcon />
                  </div>
                  <div className="ml-4 flex-grow">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                      Total Users
                    </p>
                    <h4 className="text-xl font-semibold mb-0">
                      {activeUsers}
                    </h4>
                  </div>
                  {/* <div className="flex items-center"> */}
                  {/* <span className="bg-green-50 text-m_blue text-xs px-2 py-1 rounded-full flex items-center">
                      <a className="mr-1" href="/admin/users">
                        View
                      </a>
                    </span> */}
                  {/* </div> */}
                </div>
              </div>

              {/* Total Change Card */}
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-m_black rounded-full text-blue-600">
                    <MarketsIcon />
                  </div>
                  <div className="ml-4 flex-grow">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                      Total Markets
                    </p>
                    <h4 className="text-xl font-semibold mb-0">
                      {activeMarkets}
                    </h4>
                  </div>
                  {/* <div className="flex items-center">
                    <span className="bg-green-50 text-m_blue text-xs px-2 py-1 rounded-full flex items-center">
                      <a className="mr-1" href="/admin/markets">
                        View
                      </a>
                    </span>
                  </div> */}
                </div>
              </div>

              {/* Day Change Card */}
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 flex items-center justify-center bg-m_black rounded-full text-blue-600">
                    <MatchesIcon />
                  </div>
                  <div className="ml-4 flex-grow">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                      Total Matches
                    </p>
                    <h4 className="text-xl font-semibold mb-0">
                      {totalMatches}
                    </h4>
                  </div>
                  {/* <div className="flex items-center">
                    <span className="bg-green-50 text-m_blue text-xs px-2 py-1 rounded-full flex items-center">
                      <a className="mr-1" href="/admin/matches">
                        View
                      </a>
                    </span>
                  </div> */}
                </div>
              </div>
            </div>

            {/* Bar Chart */}
            <div className="bg-white rounded-lg shadow-md flex-grow">
              <div className="p-4 border-b border-gray-100">
                <h4 className="text-lg font-medium text-gray-900 mb-0">
                  BetType Distribution
                </h4>
              </div>
              <div className="p-4 flex-grow" style={{ minHeight: "500px" }}>
                <ReactApexChart
                  key={`bar-${selectedRange}-${customStart}-${customEnd}`}
                  options={{
                    ...barChartOptions,
                    chart: {
                      ...barChartOptions.chart,
                      parentHeightOffset: 0,
                    },
                  }}
                  series={barChartSeries}
                  type="bar"
                  height="100%"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Matches Table */}
      <div className="mt-6">
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-4 border-b border-gray-100">
            <h4 className="text-lg font-medium text-gray-900 mb-0">
              Recent Matches
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-3 text-left text-md font-semibold text-gray-600 uppercase">
                    Match Name
                  </th>
                  <th className="px-4 py-3 text-left text-md font-semibold text-gray-600 uppercase">
                    Draw Date
                  </th>
                  <th className="px-4 py-3 text-left text-md font-semibold text-gray-600 uppercase">
                    Open Time
                  </th>
                  <th className="px-4 py-3 text-left text-md font-semibold text-gray-600 uppercase">
                    Close Time
                  </th>
                  {/* <th className="px-4 py-3 text-left text-md font-semibold text-gray-600 uppercase">
                    Status
                  </th> */}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recentMatches.length > 0 ? (
                  recentMatches.map((match, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {match.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {/* {new Date(match.draw_date).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })} */}
                        {match.draw_date}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatTimeTo12Hour(match.open_time)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatTimeTo12Hour(match.close_time)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center text-sm text-gray-500 py-6"
                    >
                      No matches found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-gray-100 flex justify-end">
            <a
              href="/admin/matches"
              className="text-sm text-m_blue hover:underline"
            >
              View All Matches →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
